import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { useAccount } from 'wagmi'
import {
  RequestStatus,
  RequestStatusClaimable,
  RequestStatusPending,
} from '../Withdrawal/types'
import { MAX_WITHDRAWAL_SHOWN_REQUEST_PER_TYPE } from '../constants';
import { useLidoSDK } from './useLidoSDK'
import { Address } from 'viem'

export type WithdrawalData = {
  pendingRequests: RequestStatusPending[];
  sortedClaimableRequests: RequestStatusClaimable[];
  pendingCount: number;
  readyCount: number;
  claimedCount: number;
  pendingAmountOfStETH: bigint;
  claimableAmountOfStETH: bigint;
  claimableAmountOfETH: bigint;
  isClamped: boolean;
};

export const useWithdrawalData = () => {
  const { withdraw } = useLidoSDK();
  const { address: account } = useAccount()

  const queryKey = useMemo(
    () => (account ? ['lido-withdrawal-data', account] : null),
    [account],
  );

  const fetcher = useCallback(async () => {
    if (!account) throw new Error('[useWithdrawalData] The "account" must be defined');

    const [requestIds, lastCheckpointIndex] = await Promise.all([
      withdraw.views
        .getWithdrawalRequestsIds({ account })
        .then((ids) => [...ids].sort((aId, bId) => (aId > bId ? 1 : -1))),
      withdraw.views.getLastCheckpointIndex(),
    ]);

    const STATUS_BATCH_SIZE = 500;
    const requestStatuses = [];

    for (let i = 0; i < requestIds.length; i += STATUS_BATCH_SIZE) {
      const batch = requestIds.slice(i, i + STATUS_BATCH_SIZE);
      const batchStatuses = await withdraw.views.getWithdrawalStatus({
        requestsIds: batch,
      });
      requestStatuses.push(...batchStatuses);
    }

    const claimableRequests: RequestStatus[] = [];
    const pendingRequests: RequestStatusPending[] = [];
    const pendingRequestsIds: bigint[] = [];

    requestStatuses.forEach((request, index) => {
      if (!request.isFinalized) {
        pendingRequestsIds.push(requestIds[index]);
      }
    });

    let wqRequests: { finalizationAt: string; id: string | undefined }[] = [];

    if (pendingRequestsIds.length > 0) {
      try {
        const requests =
          await withdraw.waitingTime.getWithdrawalWaitingTimeByRequestIds({
            ids: pendingRequestsIds,
          });

        wqRequests = requests
          .filter((request) => request?.requestInfo)
          .map((request) => ({
            id: request.requestInfo.requestId,
            finalizationAt: request.requestInfo.finalizationAt,
          }));
      } catch (e) {
        console.warn(
          `[useWithdrawalData] Failed to fetch request time for requests ids: ${pendingRequestsIds}. Details:`,
          e,
        );
      }
    }

    let pendingAmountOfStETH = 0n;
    let claimableAmountOfStETH = 0n;

    requestStatuses.forEach((request, index) => {
      const id = requestIds[index];
      const req: RequestStatus = {
        ...request,
        finalizationAt: null,
      };

      if (request.isFinalized && !request.isClaimed) {
        claimableRequests.push(req);
        claimableAmountOfStETH += request.amountOfStETH;
      } else if (!request.isFinalized) {
        const r = wqRequests.find((r) => r.id === id.toString());
        pendingRequests.push({
          ...req,
          finalizationAt: r?.finalizationAt ?? null,
          expectedEth: req.amountOfStETH,
        });
        pendingAmountOfStETH += request.amountOfStETH;
      }
    });

    let isClamped =
      claimableRequests.splice(MAX_WITHDRAWAL_SHOWN_REQUEST_PER_TYPE).length > 0;
    isClamped ||=
      pendingRequests.splice(MAX_WITHDRAWAL_SHOWN_REQUEST_PER_TYPE).length > 0;

    let claimableAmountOfETH = 0n;
    let sortedClaimableRequests: RequestStatusClaimable[] = [];

    if (claimableRequests.length > 0) {
      const claimableRequestIds = claimableRequests.map(({ id }) => id);
      const hints = await withdraw.views.findCheckpointHints({
        sortedIds: claimableRequestIds,
        firstIndex: 1n,
        lastIndex: lastCheckpointIndex,
      });

      const claimableEth = await withdraw.views.getClaimableEther({
        sortedIds: claimableRequestIds,
        hints,
      });

      sortedClaimableRequests = claimableRequests.map((request, index) => {
        claimableAmountOfETH += claimableEth[index];
        return {
          ...request,
          hint: hints[index],
          claimableEth: claimableEth[index],
        };
      });
    }

    return {
      pendingRequests,
      sortedClaimableRequests,
      pendingCount: pendingRequests.length,
      readyCount: sortedClaimableRequests.length,
      claimedCount: claimableRequests.length,
      pendingAmountOfStETH,
      claimableAmountOfStETH,
      claimableAmountOfETH,
      isClamped,
    };
  }, [account, withdraw]);

  const { data, mutate } = useSWR(queryKey, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })

  return {
    data,
    mutate,
  };
};
